import { X } from "@styled-icons/boxicons-regular";
import { observer } from "mobx-react-lite";
import { User } from "revolt.js";
import styled from "styled-components";
import styles from "../../../pages/contacts/Contact.module.scss";
import { Text } from "preact-i18n";
import { useCallback, useMemo, useState } from "preact/hooks";

import {
    Column,
    IconButton,
    Modal,
    TextField,
    useDebounceCallback,
} from "@revoltchat/ui";

import { noopTrue } from "../../../lib/js";

import UserCheckbox from "../../../components/common/user/UserCheckbox";
import UserIcon from "../../../components/common/user/UserIcon";
import { Username } from "../../../components/common/user/UserShort";
import { useClient } from "../../client/ClientController";
import { ModalProps } from "../types";

const List = styled.div`
    max-width: 100%;
    height: 360px;
    overflow-y: auto;
`;

function matchName(user: User, filter: string) {
    return user.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase());
}

export default observer(
    ({ callback, omit, ...props }: ModalProps<"user_picker">) => {
        const [selected, setSelected] = useState<string[]>([]);
        const [filter, _setFilter] = useState("");

        const omitted = useMemo(
            () => new Set([...(omit || []), "00000000000000000000000000"]),
            [omit],
        );

        const setFilter = useDebounceCallback(
            (value) => _setFilter(value as string),
            [_setFilter],
            400,
        );

        const onClearFilter = () => _setFilter("");

        const client = useClient();
        const users = client.users.contactList.filter(
            (x) => !!x.ldap_info && !x.ldap_info?.retired,
        );

        const selectedUsers = useMemo(() => {
            const allSelected = new Set(selected);

            return users.filter((usr) => allSelected.has(usr._id));
        }, [users, selected]);

        const searchedUsers = useMemo(() => {
            return users.filter((usr) => matchName(usr, filter));
        }, [users, filter]);

        const handleUserToggle = useCallback(
            (userId: string, isSelected: boolean) => {
                setSelected((prev) =>
                    isSelected
                        ? [...prev, userId]
                        : prev.filter((id) => id !== userId),
                );
            },
            [],
        );

        return (
            <Modal
                {...props}
                padding={"5px 1rem 1rem"}
                title={<Text id="app.special.popovers.user_picker.select" />}
                actions={[
                    {
                        children: <Text id="app.special.modals.actions.ok" />,
                        onClick: () => callback(selected).then(noopTrue),
                    },
                ]}>
                <Column>
                    <TextField
                        value={filter}
                        onChange={setFilter}
                        placeholder="Поиск..."
                        rightAddon={
                            <IconButton
                                className={styles.clearBtn}
                                onClick={onClearFilter}
                                children={<X size={24} />}
                            />
                        }
                    />
                    <UserBadgeList>
                        {selectedUsers.map((usr) => (
                            <UserBadge
                                key={usr._id}
                                user={usr}
                                onClick={() => {
                                    setSelected((users) =>
                                        users.filter((id) => id !== usr._id),
                                    );
                                }}
                            />
                        ))}
                    </UserBadgeList>
                    <List>
                        {searchedUsers.map((user) => (
                            <UserCheckbox
                                key={user._id}
                                user={user}
                                value={selected.includes(user._id)}
                                onChange={(v) => handleUserToggle(user._id, v)}
                            />
                        ))}
                    </List>
                </Column>
            </Modal>
        );
    },
);

const UserBadgeBase = styled.div`
    padding: 5px;
    opacity: 0.7;
    max-width: 100px;
    display: inline-block;

    transition: all 0.3s ease-in-out;

    position: relative;

    &:hover {
        opacity: 1;
    }

    &:hover .close-icon {
        opacity: 1;
    }
`;

const UserBadgeContent = styled.div`
    padding: 5px;
    display: flex;
    align-items: center;
    gap: 0.5em;
    border-radius: 15px;
    background-color: var(--secondary-background);
    border: var(--accent-contrast) 1px solid;
    cursor: pointer;
`;

type UserBadgeProps = {
    user: User;
    onClick: () => void;
};

function UserBadge({ user, onClick }: UserBadgeProps) {
    return (
        <UserBadgeBase>
            <UserBadgeContent onClick={onClick} role="button">
                <UserIcon target={user} size={16} />
                <Username user={user} maxLength={8} />
            </UserBadgeContent>
        </UserBadgeBase>
    );
}

const UserBadgeList = styled.div`
    height: 100%;
    max-height: 80px;
    overflow: auto;
`;
